// @ts-strict

import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { AppDefaultLanguage } from '@app/core/helpers/constants';
import { IPagination } from '@app/core/models/core-models';
import { TranslateService } from '@ngx-translate/core';
import { isEqual } from 'lodash';
import { Subject } from 'rxjs';
import { debounceTime, map, takeUntil, tap } from 'rxjs/operators';
import { NotificationTypes } from '@app/core/services/global-notifications/global-notifications.model';
import { GlobalNotificationsService } from '@app/core/services/global-notifications/global-notifications.service';

@Component({
  selector: 'app-global-pagination-component',
  templateUrl: './global-pagination-component.component.html',
  styleUrls: ['./global-pagination-component.component.scss']
})
export class GlobalPaginationComponentComponent implements OnInit, OnDestroy, OnChanges {
  @Input() page?: number;
  @Input() pageSize?: number;
  @Input() pagination?: IPagination;
  @Input() inputPage = 0;
  @Output() emitPageChange = new EventEmitter<number>();

  constructor(
    private readonly translate: TranslateService,
    private readonly globalNotificationsService: GlobalNotificationsService
  ) {}

  public currentPanelLang: string = AppDefaultLanguage.short;
  public inputControl!: UntypedFormControl;
  private destroy$ = new Subject<void>();

  ngOnInit(): void {
    this.currentPanelLang = this.translate.currentLang;
    this.inputControl = new UntypedFormControl([this.pagination?.current_page]);

    this.inputControl.valueChanges
      .pipe(
        map(val => {
          if (this.pagination?.total_pages && val > this.pagination?.total_pages) {
            this.showNotification('view_translations.pagination_max', { max: this.pagination.total_pages });
            return this.pagination?.total_pages;
          } else {
            return val;
          }
        }),
        debounceTime(500),
        tap(val => {
          this.onPageChange(val);
        }),
        takeUntil(this.destroy$)
      )
      .subscribe();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes.pagination?.previousValue &&
      changes.pagination?.currentValue &&
      !isEqual(changes.pagination?.previousValue.current_page, changes.pagination?.currentValue.current_page)
    ) {
      this.inputControl.setValue(changes.pagination?.currentValue.current_page, { emitEvent: false });
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  convertToNumber(value: string): number {
    return Number(value);
  }

  public onPageChange(pageNo: number): void {
    this.page = pageNo;
    this.inputControl.setValue(pageNo, { emitEvent: false });
    this.inputPage = this.page;
    this.emitPageChange.emit(this.page);
  }

  public changePageOnEnter(event: KeyboardEvent): void {
    if (this.inputPage && event.key === 'Enter' && this.inputPage <= (this.pagination?.total_pages || 1)) {
      this.onPageChange(this.inputPage);
    }
  }

  public redirectToLastPage(event?: Event): void {
    event?.preventDefault();

    if (this.pagination && this.inputPage === this.pagination.total_pages) {
      return;
    }
    this.page = this.pagination?.total_pages || 1;
    this.inputPage = this.page;
    this.onPageChange(this.page);
  }

  private showNotification(message: string, translationParams?: Record<string, any>): void {
    const translatedMessage = this.translate.instant(message, translationParams);
    this.globalNotificationsService.show({
      message: translatedMessage,
      type: NotificationTypes[1],
      translate: false,
      isPersistent: false,
      id: ''
    });
  }
}
